import type { DataGridFilterConfig, DataGridLeafColumn } from '@ibsys/kit';
import type { ChartValueColorRule, CustomChartTableColumn, CustomChartTableRow } from '@/entities/dashboards';
import { fmt, slicePaletteBgClassByCode } from '../../utils';
import { chartValueRuleColor } from '../chartElements';
import { chartDatePrecision, formatChartDate, isChartDateType } from '../chartValues';

/** Ширина колонки таблицы: числовая уже текстовой — в ней значение, а не название. */
const TEXT_COLUMN_SIZE = 220;
const NUMBER_COLUMN_SIZE = 140;

/** Ширина колонки с датой: запись `дд.мм.гггг чч:мм:сс` шире числа, но уже названия. */
const DATE_COLUMN_SIZE = 180;

/** Строка таблицы вместе с её порядковым ключом: значения строк могут совпадать целиком. */
export type IndexedTableRow = { key: string; values: CustomChartTableRow };

/** Ширина колонки по типу её значений. */
const columnSize = (type: CustomChartTableColumn['type']): number => {
  if (type === 'number') return NUMBER_COLUMN_SIZE;
  return isChartDateType(type) ? DATE_COLUMN_SIZE : TEXT_COLUMN_SIZE;
};

/** Поле отбора по колонке отвечает виду её значений: текст, число или дата. */
const columnFilter = (type: CustomChartTableColumn['type']): DataGridFilterConfig => {
  if (type === 'number') return { type: 'integer' };
  if (type === 'date') return { type: 'date-range' };
  if (type === 'timestamp') return { type: 'timestamp-range' };
  return { type: 'text' };
};

/** Скругление заливки числовой ячейки — тем же радиусом, каким скруглены блоки показателей. */
const VALUE_CELL_SHAPE_CLASS = 'rounded-lg';

/**
 * Строит определения колонок DataGrid по описанию колонок ответа.
 *
 * Значение колонки-даты показывается в виде, принятом для пользователя, а упорядочивается и
 * отбирается по машинной записи: она и остаётся значением ячейки, а вид задаётся только показом.
 * Ряд, по которому берётся точность времени, — сама колонка: значения одной колонки написаны
 * одинаково, а соседняя колонка дат может быть написана иначе.
 *
 * Числовое значение окрашивается правилами раскраски по величине, если пользователь их задал:
 * цвет остаётся у самого числа, а не у всей строки — правило говорит о величине.
 */
export const buildTableColumns = (
  columns: CustomChartTableColumn[],
  rows: CustomChartTableRow[],
  valueColorRules?: ChartValueColorRule[],
): DataGridLeafColumn<IndexedTableRow>[] =>
  columns.map((column) => {
    const precision = chartDatePrecision(
      rows.map((row) => row[column.code]),
      column.type,
    );

    return {
      id: column.code,
      header: column.title,
      accessor: (row) => row.values[column.code] ?? null,
      size: columnSize(column.type),
      align: column.type === 'number' ? 'center' : undefined,
      enableSorting: true,
      enableFiltering: true,
      filter: columnFilter(column.type),
      cellClassName:
        column.type === 'number' && valueColorRules?.length
          ? ({ row }) => {
              const value = row.values[column.code];
              const color = chartValueRuleColor(typeof value === 'number' ? value : null, valueColorRules);
              return color ? `${VALUE_CELL_SHAPE_CLASS} ${slicePaletteBgClassByCode(color)}` : '';
            }
          : undefined,
      cell: ({ value }) => {
        if (column.type === 'number') return fmt(value as number | null);
        if (value === null || value === undefined) return '';
        return isChartDateType(column.type) ? formatChartDate(String(value), column.type, precision) : String(value);
      },
    };
  });

/** Приписывает строкам ключ по их позиции — одинаковые по значениям строки остаются различимы. */
export const toIndexedRows = (rows: CustomChartTableRow[], prefix = ''): IndexedTableRow[] =>
  rows.map((values, index) => ({ key: `${prefix}${index}`, values }));
